import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as SentryAngular from '@sentry/angular';
import { omit, toNumber } from 'lodash';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DeferredTimer } from '../core/deffered-timer';
import { AppStateLoaderService } from '../data/app-state-loader.service';
import { Settings, User } from '../data/models';
import { APP_SETTINGS } from '../data/settings-data.provider';
import { LOCAL_STORAGE } from '../data/storage';

/** If page is leaved for more than several minutes, user needs to log in again */
const ALLOWED_AWAY_MINUTES = 3;

const LOGOUT_COUNTDOWN_MINUTES = 5;


@Injectable({ providedIn: 'root' })
export class AuthUserService {
  private readonly STORAGE_AUTH_USER_ID_KEY = 'auth_user_id';
  private readonly STORAGE_PAGE_LEAVED_TIME_KEY = 'page_leaved_at';
  public pickupSubscription: Subscription = new Subscription();
  public reservationSubscription: Subscription = new Subscription();
  public printerStatusSubscription: Subscription = new Subscription();

  autoLogoutTimer: DeferredTimer;

  _authenticatedUser: User | null = null;
  set authenticatedUser(user: User | null) {
    this._authenticatedUser = user;
    SentryAngular.setExtra('user', omit(user, 'gravatarUrl', 'pin'));
  }
  get authenticatedUser(): User | null {
    return this._authenticatedUser;
  }

  constructor(@Inject(LOCAL_STORAGE) private readonly storage: Storage,
    @Inject(APP_SETTINGS) private readonly settings: Settings,
    private readonly stateLoader: AppStateLoaderService,
    private readonly router: Router,
    private readonly modalService: NgbModal) {
    this.autoLogoutTimer = this.registerAutoLogoutTimer();
    this.autoLogoutAfterAway();
    this.checkAuthenticatedUser();
    this.registerLeaveEvent();
  }

  login(user: User, credentials: { pin: string }): boolean {
    if (user.pin !== credentials.pin) {
      return false;
    }

    this.storage.setItem(this.STORAGE_AUTH_USER_ID_KEY, `${user.id}`);
    this.authenticatedUser = user;
    this.router.navigate(['/']);
    return true;
  }

  isAuthenticated(): boolean {
    return !!this.authenticatedUser;
  }

  logout() {
    this.storage.removeItem(this.STORAGE_AUTH_USER_ID_KEY);
    this.authenticatedUser = null;
    this.stateLoader.clear();
    this.autoLogoutTimer.abort();
    // unsubscribe from data polling
    this.pickupSubscription?.unsubscribe();
    this.reservationSubscription?.unsubscribe();
    this.printerStatusSubscription?.unsubscribe();
    // close all modals
    this.modalService.dismissAll();

    this.router.navigate(['/login']);
  }

  /** Restore authenticated user on service startup */
  private checkAuthenticatedUser() {
    const userId = this.storage.getItem(this.STORAGE_AUTH_USER_ID_KEY);
    if (!userId) {
      return;
    }

    const user = this.settings.users.find(u => u.id === toNumber(userId));
    if (!user) {
      // If logged-in user not found remove stored credentials and redirect to login page
      this.logout();
      return;
    }

    this.authenticatedUser = user;
    this.autoLogoutTimer.start();
  }

  /** Logout user if it was away for more than several minutes */
  private autoLogoutAfterAway() {
    const pageLeavedAt = this.storage.getItem(this.STORAGE_PAGE_LEAVED_TIME_KEY);
    this.storage.removeItem(this.STORAGE_PAGE_LEAVED_TIME_KEY);

    if (!pageLeavedAt || DateTime.fromMillis(toNumber(pageLeavedAt)).plus({ minute: ALLOWED_AWAY_MINUTES }) < DateTime.now()) {
      this.logout();
    }
  }

  /** Register when page is leaved */
  private registerLeaveEvent() {
    window.addEventListener('beforeunload', () => {
      if (this.authenticatedUser) {
        this.storage.setItem(this.STORAGE_PAGE_LEAVED_TIME_KEY, Date.now().toString());
      }
    });
  }

  private registerAutoLogoutTimer(): DeferredTimer {
    const autoLogoutTimer = new DeferredTimer(() => this.logout(), ALLOWED_AWAY_MINUTES, LOGOUT_COUNTDOWN_MINUTES);

    const eventsToResetTimer: (keyof DocumentEventMap)[] = [
      'click', 'wheel', 'mousemove', 'touchmove', 'mousedown', 'keypress', 'scroll'
    ];

    eventsToResetTimer.forEach(evt => {
      document.addEventListener(evt, () => {
        autoLogoutTimer.restart();
      });
    });

    return autoLogoutTimer;
  }
}
